import { format } from "date-fns";
import {
  EmploymentIndustry,
  EmploymentOccupation,
  SalaryCycle,
} from "../../lead-api";
import {
  type MXEmploymentMenuItem,
  type MXIndustryMenuItem,
  type SalaryCycleMenuItem,
  type SalaryCycleValueMenuItem,
} from "../Employment.types";

export const employmentMenuItemsMX: MXEmploymentMenuItem[] = [
  {
    label: "inputs.employment.self_employed",
    value: EmploymentOccupation.SelfEmployed,
  },
  { label: "inputs.employment.employed", value: EmploymentOccupation.Employed },
  {
    label: "inputs.employment.business_owner",
    value: EmploymentOccupation.BusinessOwner,
  },
  {
    label: "inputs.employment.freelancer",
    value: EmploymentOccupation.Freelancer,
  },
  {
    label: "inputs.employment.not_employed",
    value: EmploymentOccupation.Unemployed,
  },
  {
    label: "inputs.employment.retired",
    value: EmploymentOccupation.Retired,
  },
  {
    label: "inputs.employment.student",
    value: EmploymentOccupation.Student,
  },
  {
    label: "inputs.employment.housewife",
    value: EmploymentOccupation.Housewife,
  },
];

export const industryMenuItemsMX: MXIndustryMenuItem[] = [
  {
    label: "inputs.employment.industry.administration_office",
    value: EmploymentIndustry.AdministrationOffice,
  },
  {
    label: "inputs.employment.industry.agriculture_forestry",
    value: EmploymentIndustry.AgricultureForestry,
  },
  {
    label: "inputs.employment.industry.food_entertainment",
    value: EmploymentIndustry.FoodEntertainment,
  },
  {
    label: "inputs.employment.industry.manufacturing_production_construction",
    value: EmploymentIndustry.ManufacturingProductionConstruction,
  },
  {
    label: "inputs.employment.industry.transportation_logistics",
    value: EmploymentIndustry.TransportationLogistics,
  },
  {
    label: "inputs.employment.industry.automotive",
    value: EmploymentIndustry.Automotive,
  },
  {
    label: "inputs.employment.industry.trade",
    value: EmploymentIndustry.Trade,
  },
  {
    label: "inputs.employment.industry.construction",
    value: EmploymentIndustry.Construction,
  },
  {
    label: "inputs.employment.industry.culture",
    value: EmploymentIndustry.Culture,
  },
  {
    label: "inputs.employment.industry.education_research",
    value: EmploymentIndustry.Education,
  },
  {
    label: "inputs.employment.industry.hotels_and_tourism",
    value: EmploymentIndustry.HotelsAndTourism,
  },
  {
    label: "inputs.employment.industry.engineering",
    value: EmploymentIndustry.Engineering,
  },
  {
    label: "inputs.employment.industry.real_estate",
    value: EmploymentIndustry.RealEstate,
  },
  {
    label: "inputs.employment.industry.media",
    value: EmploymentIndustry.Media,
  },
  {
    label: "inputs.employment.industry.military",
    value: EmploymentIndustry.Military,
  },
  {
    label: "inputs.employment.industry.mining",
    value: EmploymentIndustry.Mining,
  },
  {
    label: "inputs.employment.industry.oil_and_gas",
    value: EmploymentIndustry.OilAndGas,
  },
  {
    label: "inputs.employment.industry.police",
    value: EmploymentIndustry.Police,
  },
  {
    label: "inputs.employment.industry.metal_production",
    value: EmploymentIndustry.MetalProduction,
  },
  {
    label: "inputs.employment.industry.chemistry",
    value: EmploymentIndustry.Chemistry,
  },
  {
    label: "inputs.employment.industry.healthcare_social_services",
    value: EmploymentIndustry.Healthcare,
  },
  {
    label: "inputs.employment.industry.post_and_telecommunications_service",
    value: EmploymentIndustry.PostAndTelecommunicationsService,
  },
  {
    label: "inputs.employment.industry.financial_services",
    value: EmploymentIndustry.FinancialServices,
  },
  {
    label: "inputs.employment.industry.professional_services",
    value: EmploymentIndustry.ProfessionalServices,
  },
  {
    label: "inputs.employment.industry.forestry_and_logging",
    value: EmploymentIndustry.ForestryWood,
  },
  {
    label: "inputs.employment.industry.tobacco",
    value: EmploymentIndustry.Tobacco,
  },
  {
    label: "inputs.employment.industry.textiles",
    value: EmploymentIndustry.Textiles,
  },
  {
    label: "inputs.employment.industry.air_transportation",
    value: EmploymentIndustry.AirTransportation,
  },
  {
    label: "inputs.employment.industry.customs_maritime_transport",
    value: EmploymentIndustry.CustomsMaritimeTransport,
  },
];

export const salaryCycleMenuItems: SalaryCycleMenuItem[] = [
  {
    label: "inputs.employment.salary_cycle.weekly",
    value: SalaryCycle.Weekly,
  },
  {
    label: "inputs.employment.salary_cycle.semi_monthly",
    value: SalaryCycle.SemiMonthly,
  },
  {
    label: "inputs.employment.salary_cycle.biweekly",
    value: SalaryCycle.Biweekly,
  },
  {
    label: "inputs.employment.salary_cycle.monthly",
    value: SalaryCycle.Monthly,
  },
];

export const getSalaryCycleValueMenuItems = (
  salaryCycle: SalaryCycle,
): SalaryCycleValueMenuItem[] => {
  switch (salaryCycle) {
    case SalaryCycle.Weekly:
      return getSalaryCycleDateMenuItems(new Date(), 7);
    case SalaryCycle.Biweekly:
      return getSalaryCycleDateMenuItems(new Date(), 14);
    case SalaryCycle.SemiMonthly:
      return getSalaryCycleSemiMonthlyMenuItems();
    default:
      return getSalaryCycleMonthlyItems();
  }
};

const getSalaryCycleDateMenuItems = (
  curentDate: Date,
  daysToDisplay: number,
): SalaryCycleValueMenuItem[] => {
  const menuItems: SalaryCycleValueMenuItem[] = [];

  while (daysToDisplay !== 0) {
    curentDate.setDate(curentDate.getDate() + 1);
    const date = format(curentDate, "dd/MM/yyyy");
    menuItems.push({
      label: date,
      value: date,
    });
    daysToDisplay--;
  }

  return menuItems;
};

const SEMI_MONTHLY_VALUES = [
  "5 y 20",
  "10 y 25",
  "13 y 28",
  "14 y 29",
  "15 y 30",
];

const getSalaryCycleSemiMonthlyMenuItems = (): SalaryCycleValueMenuItem[] => {
  return SEMI_MONTHLY_VALUES.map((value) => {
    return {
      label: value,
      value,
    };
  });
};

const getSalaryCycleMonthlyItems = (): SalaryCycleValueMenuItem[] => {
  const menuItems: SalaryCycleValueMenuItem[] = [];

  for (let i = 1; i < 32; i++) {
    const value = i.toString();
    menuItems.push({
      label: value,
      value,
    });
  }

  return menuItems;
};
